import { useEffect, useMemo, useState } from "react";
import { TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert } from "@mui/material";
import axios from "axios";
import Swal from "sweetalert2";

const REMINDER_TYPE_MAP = {
    first: {
        title: "First Reminder",
        endpoint: "first-reminder",
        daysField: "first_reminder_days_before",
        subjectField: "first_reminder_subject",
        bodyField: "first_reminder_body",
        noteField: "first_reminder_product_note",
    },
    second: {
        title: "Second Reminder",
        endpoint: "second-reminder",
        daysField: "second_reminder_days_before",
        subjectField: "second_reminder_subject",
        bodyField: "second_reminder_body",
        noteField: "second_reminder_product_note",
    },
    expired: {
        title: "Expired Reminder",
        endpoint: "expired-reminder",
        daysField: "expired_reminder_days_after",
        subjectField: "expired_reminder_subject",
        bodyField: "expired_reminder_body",
        noteField: "expired_reminder_product_note",
    },
};

export default function ReminderConfigForm({ open, handleClose, rowData, reminderType, fetchData }) {
    const reminderConfig = useMemo(() => REMINDER_TYPE_MAP[reminderType] || REMINDER_TYPE_MAP.first, [reminderType]);
    const daysLabel = reminderType === "expired" ? "Days After Expiry" : "Days Before Expiry";
    const daysHelperText =
        reminderType === "expired"
            ? "Enter how many days after expiry to send this reminder. Leave empty to disable this reminder."
            : "Enter how many days before expiry to send this reminder. Leave empty to disable this reminder.";

    const [formData, setFormData] = useState({
        reminder_days: "",
        reminder_subject: "",
        reminder_body: "",
        reminder_product_note: "",
    });
    const [errors, setErrors] = useState({ reminder_subject: false, reminder_body: false });
    const [helperText, setHelperText] = useState({ reminder_subject: "", reminder_body: "" });
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (open && rowData) {
            const nextDaysValue = rowData[reminderConfig.daysField];
            setFormData({
                reminder_days: nextDaysValue === null || nextDaysValue === undefined ? "" : String(nextDaysValue),
                reminder_subject: rowData[reminderConfig.subjectField] || "",
                reminder_body: rowData[reminderConfig.bodyField] || "",
                reminder_product_note: rowData[reminderConfig.noteField] || "",
            });
            setErrors({ reminder_subject: false, reminder_body: false });
            setHelperText({ reminder_subject: "", reminder_body: "" });
            setErrorMessage("");
        }
    }, [open, rowData, reminderConfig]);

    if (!rowData) return null;

    const setError = async (field, message) => {
        await setErrors((prevErrors) => ({ ...prevErrors, [field]: true }));
        await setHelperText((prevHelperText) => ({ ...prevHelperText, [field]: message }));
    };

    const clearError = async (field) => {
        await setErrors((prevErrors) => ({ ...prevErrors, [field]: false }));
        await setHelperText((prevHelperText) => ({ ...prevHelperText, [field]: "" }));
    };

    const handleSave = async () => {
        const isDisabled = formData.reminder_days === "";

        if (!isDisabled && !formData.reminder_subject.trim()) {
            await setError("reminder_subject", "Subject is required when days is set.");
        } else {
            await clearError("reminder_subject");
        }

        if (!isDisabled && !formData.reminder_body.trim()) {
            await setError("reminder_body", "Body is required when days is set.");
        } else {
            await clearError("reminder_body");
        }

        if (!isDisabled && (!formData.reminder_subject.trim() || !formData.reminder_body.trim())) {
            await setErrorMessage("Please complete all required reminder fields.");
            return;
        }

        try {
            const payload = isDisabled
                ? { reminder_days: null }
                : {
                      reminder_days: Number(formData.reminder_days),
                      reminder_subject: formData.reminder_subject,
                      reminder_body: formData.reminder_body,
                      reminder_product_note: formData.reminder_product_note,
                  };

            const response = await axios.patch(
                process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/product-groups/${rowData.product_group_id}/${reminderConfig.endpoint}`,
                payload
            );

            if (response.status === 200) {
                handleClose();
                Swal.fire({
                    title: "Success",
                    text: `${reminderConfig.title} updated successfully`,
                    icon: "success",
                    showConfirmButton: false,
                    timer: 2000,
                });
                fetchData();
            }
        } catch (error) {
            if (error.response && error.response.data) {
                setErrorMessage(error.response.data.message);
            } else {
                setErrorMessage(`An error occurred while updating ${reminderConfig.title.toLowerCase()}. Please try again later.`);
            }
        }
    };

    return (
        <Dialog
            fullWidth
            maxWidth="md"
            open={open}
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">{`Configure ${reminderConfig.title}`}</DialogTitle>
            {errorMessage && (
                <Alert variant="outlined" severity="error" style={{ marginLeft: "24px", marginRight: "24px" }}>
                    {errorMessage}
                </Alert>
            )}
            <DialogContent style={{ paddingBottom: "0px" }}>
                <TextField
                    autoFocus
                    autoComplete="off"
                    margin="dense"
                    id="reminder_days"
                    label={daysLabel}
                    type="number"
                    fullWidth
                    value={formData.reminder_days}
                    helperText={daysHelperText}
                    onChange={(e) => {
                        setFormData({ ...formData, reminder_days: e.target.value });
                    }}
                />
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="reminder_subject"
                    label="Subject"
                    type="text"
                    fullWidth
                    value={formData.reminder_subject}
                    error={errors.reminder_subject}
                    helperText={helperText.reminder_subject}
                    onChange={(e) => {
                        setFormData({ ...formData, reminder_subject: e.target.value });
                    }}
                />
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="reminder_body"
                    label="Body"
                    type="text"
                    multiline
                    rows={8}
                    fullWidth
                    value={formData.reminder_body}
                    error={errors.reminder_body}
                    helperText={helperText.reminder_body}
                    onChange={(e) => {
                        setFormData({ ...formData, reminder_body: e.target.value });
                    }}
                />
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="reminder_product_note"
                    label="Product Note"
                    type="text"
                    multiline
                    rows={4}
                    fullWidth
                    value={formData.reminder_product_note}
                    onChange={(e) => {
                        setFormData({ ...formData, reminder_product_note: e.target.value });
                    }}
                />
            </DialogContent>
            <DialogActions style={{ margin: "12px" }}>
                <Button onClick={handleSave} variant="contained" color="primary" style={{ marginRight: "6px", color: "white" }}>
                    Save
                </Button>
                <Button onClick={handleClose} variant="contained" color="error" style={{ marginRight: "6px", color: "white" }}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
